// src/hooks/useUnsatisfiedClients.ts
// Bloque B — re-cableado de Elasticsearch (esapi) a lealify-base.
// Feed paginado de VISITAS con feedback malo/neutral en N días. Antes consumía
// ElasticsearchService.searchUnsatisfiedClients (shape ES crudo); ahora usa
// analyticsApi.unsatisfiedVisits (GET /analytics/unsatisfied-visits). Misma API
// pública e interfaz UnsatisfiedVisit → UnsatisfiedClientsPage queda intacta.

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useError } from '../context/ErrorContext';
import { analyticsApi } from '../api/analyticsApi';

// Interfaces
export interface UnsatisfiedVisit {
  id: string;
  clientId: string;
  clientName: string | null;
  clientPhone: string;
  branchId?: string;
  branchName?: string;
  businessId: string;
  checkInDate: Date;
  feedback: 'neutral' | 'malo';
  earnedPoints: number;
  countryCode?: string;
}

interface PaginationProps {
  currentPage: number;
  hasMore: boolean;
  onNextPage: () => void;
  onPrevPage: () => void;
  canGoPrev: boolean;
  isLoading: boolean;
  itemsPerPage: number;
  countOnPage: number;
  totalItems: number;
}

// Hook options
interface UseUnsatisfiedClientsOptions {
  itemsPerPage?: number;
  daysPeriod?: number;
  feedbackTypes?: Array<'neutral' | 'malo'>;
}

const DEFAULT_ITEMS_PER_PAGE = 20;
const DEFAULT_DAYS_PERIOD = 30;
const DEFAULT_FEEDBACK_TYPES: Array<'neutral' | 'malo'> = ['neutral', 'malo'];

export const useUnsatisfiedClients = (options: UseUnsatisfiedClientsOptions = {}) => {
  const { currentUser } = useAuth();
  const { clearErrors } = useError();

  const ITEMS_PER_PAGE = options.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE;
  const DAYS_PERIOD = options.daysPeriod ?? DEFAULT_DAYS_PERIOD;
  const FEEDBACK_TYPES = options.feedbackTypes ?? DEFAULT_FEEDBACK_TYPES;

  // States
  const [unsatisfiedVisits, setUnsatisfiedVisits] = useState<UnsatisfiedVisit[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalHits, setTotalHits] = useState<number>(0);
  const [countOnCurrentPage, setCountOnCurrentPage] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Computed properties for pagination
  const hasMore = currentPage * ITEMS_PER_PAGE < totalHits;

  // Main fetch function
  const fetchUnsatisfiedVisits = useCallback(async (pageToFetch: number) => {
    const businessId = currentUser?.businessId;
    if (!businessId) {
      setUnsatisfiedVisits([]);
      setTotalHits(0);
      setLoading(false);
      setCurrentPage(1);
      setCountOnCurrentPage(0);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    if (clearErrors) clearErrors();

    try {
      const res = await analyticsApi.unsatisfiedVisits({
        days: DAYS_PERIOD,
        page: pageToFetch,
        size: ITEMS_PER_PAGE,
        feedback: FEEDBACK_TYPES,
      });

      const items = res?.items || [];
      setTotalHits(res?.total ?? 0);
      setCountOnCurrentPage(items.length);

      const visitsList: UnsatisfiedVisit[] = items.map((row) => {
        const checkInDate = new Date(row.occurred_at);
        return {
          id: String(row.id),
          clientId: row.contact_id != null ? String(row.contact_id) : 'N/A',
          clientName: row.name || null,
          clientPhone: row.phone || '',
          branchId: row.branch_id != null ? String(row.branch_id) : undefined,
          branchName: row.branch_name || (row.branch_id != null ? '(Sin nombre)' : 'Principal'),
          businessId,
          checkInDate: isNaN(checkInDate.getTime()) ? new Date(0) : checkInDate,
          earnedPoints: Number(row.points_delta) || 0,
          feedback: row.feedback,
        };
      });

      setUnsatisfiedVisits(visitsList);
      setCurrentPage(pageToFetch);
    } catch (err: any) {
      console.error('ERROR en fetchUnsatisfiedVisits:', err);
      setError(`Error al cargar clientes insatisfechos: ${err?.message || 'desconocido'}`);
      setUnsatisfiedVisits([]);
      setTotalHits(0);
      setCurrentPage(1);
      setCountOnCurrentPage(0);
    } finally {
      setLoading(false);
    }
    // clearErrors fuera de deps a propósito (evita bucle de renders).
  }, [currentUser?.businessId, ITEMS_PER_PAGE, DAYS_PERIOD, FEEDBACK_TYPES]);

  // Pagination handlers
  const handleNextPage = useCallback(() => {
    if (hasMore && !loading) {
      fetchUnsatisfiedVisits(currentPage + 1);
    }
  }, [hasMore, loading, fetchUnsatisfiedVisits, currentPage]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1 && !loading) {
      fetchUnsatisfiedVisits(currentPage - 1);
    }
  }, [currentPage, loading, fetchUnsatisfiedVisits]);

  // Create pagination props object
  const paginationProps: PaginationProps = {
    currentPage,
    hasMore,
    onNextPage: handleNextPage,
    onPrevPage: handlePrevPage,
    canGoPrev: currentPage > 1,
    isLoading: loading,
    itemsPerPage: ITEMS_PER_PAGE,
    countOnPage: countOnCurrentPage,
    totalItems: totalHits,
  };

  // Initial load effect
  useEffect(() => {
    const currentBusinessId = currentUser?.businessId;
    if (currentBusinessId) {
      fetchUnsatisfiedVisits(1);
    } else {
      setUnsatisfiedVisits([]);
      setLoading(false);
      setCurrentPage(1);
      setTotalHits(0);
      setCountOnCurrentPage(0);
    }
  }, [currentUser?.businessId, fetchUnsatisfiedVisits]);

  return {
    unsatisfiedVisits,
    loading,
    error,
    refetchVisits: () => {
      fetchUnsatisfiedVisits(1);
    },
    paginationProps,
    currentPage,
  };
};

export default useUnsatisfiedClients;
